import { useEffect, useMemo, useState } from 'react'
import { formatInteger } from '../../../utils/formatting'
import { DEAL_TABLE_KEY } from 'constants/tableQueryKeys'
import { Tabs } from 'components/Tabs'
import { dataTableSortingStoreRepo } from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import apiService from 'services/api'
import { DealCard } from 'components/NewCompanyPage/DealCard'
import { Deal } from 'models/deals'
import { useAuth } from 'context/authentication/useAuth'

import { queryClient } from 'services/queryClient'
import { MobileListViewHeader } from 'components/MobileListViewHeader'
import { MobileInfiniteScrollView } from 'components/MobileInfiniteScrollView'
import { SortingOptionLabel } from 'components/SortSelect/SortingOptionLabel'
import { MobileEditDealModal } from 'components/Deals/MobileEditDealModal'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'

const sortingOptions = [
  {
    label: <SortingOptionLabel label="Last Updated" type="number" />,
    value: 'modified',
  },
  {
    label: <SortingOptionLabel label="Last Updated" type="number" desc />,
    value: '-modified',
  },
  {
    label: <SortingOptionLabel label="Date Added" type="number" />,
    value: 'created',
  },
  {
    label: <SortingOptionLabel label="Date Added" type="number" desc />,
    value: '-created',
  },
  { label: <SortingOptionLabel label="Company" />, value: 'company' },
  {
    label: <SortingOptionLabel label="Company" desc />,
    value: '-company',
  },
  { label: <SortingOptionLabel label="Sales Stage" />, value: 'sales_stage' },
  {
    label: <SortingOptionLabel label="Sales Stage" desc />,
    value: '-sales_stage',
  },
]

export function MobileDealsPage() {
  const [selectedTab, setSelectedTab] = useState<string>('my_deals')
  const [dealToEdit, setDealToEdit] = useState<Deal>()
  const api = apiService()
  const { user } = useAuth()

  const IDENTIFIER = 'DEALS_TABLE'

  const dealsFilterStore = filterStoreRepo.getStore(IDENTIFIER)
  const filterStore = dealsFilterStore()
  const { page, setPage } = filterStore

  // Fetch sales stages
  const { data: salesStages } = api.useGetSalesStages()

  // Fetch users for account owner names
  const { data: users } = api.useGetUsers()

  const { data: dealsCounts } = api.useGetDealsCounts()
  const sortingStore = dataTableSortingStoreRepo.getStore(DEAL_TABLE_KEY)()

  const { sorting } = sortingStore

  const filtersAsParams = {
    ...useFilterParams(dealsFilterStore),
  }

  useEffect(() => {
    if (page > 1) {
      setPage(1)
    }
  }, [selectedTab])

  const filterParams = useMemo(() => {
    const params: Record<string, unknown> = { ...filtersAsParams }
    return params
  }, [filtersAsParams])

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}

    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }

    return params
  }, [sorting])

  const filterAndSortParams = useMemo(
    () => ({
      ...filterParams,
      ...sortParams,
      ...(selectedTab === 'my_deals' ? { account_owner: user?.id } : {}),
    }),
    [filterParams, sortParams, selectedTab]
  )

  // Handle deal edit
  const handleEditDeal = (deal: Deal) => {
    setDealToEdit(deal)
  }

  const query = api.useGetDealsInfinite({
    page,
    page_size: 10,
    ...filterAndSortParams,
  })

  // Handle deal edited - invalidate queries to refetch fresh data
  const handleDealEdited = () => {
    // Invalidate deals queries to trigger refetch
    queryClient.invalidateQueries({ queryKey: ['deals'] })
  }

  return (
    <>
      <div className="w-full relative">
        <Tabs
          tabs={[
            {
              key: 'my_deals',
              title: `My Deals (${formatInteger(dealsCounts?.my_deals_count)})`,
            },
            {
              key: 'all_deals',
              title: `All Deals (${formatInteger(dealsCounts?.all_deals_count)})`,
            },
          ]}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />
      </div>

      <MobileListViewHeader
        sortingOptions={sortingOptions}
        filterStoreIdentifier={IDENTIFIER}
        tableKey={DEAL_TABLE_KEY}
        defaultSorting={[{ id: 'modified', desc: true }]}
      />

      <FilterChips
        store={dealsFilterStore}
        identifier={IDENTIFIER}
        clearAllButton
        showActive
      />

      <MobileInfiniteScrollView<{ results: Deal[] }, Deal>
        extractItems={(page) => page.results}
        infiniteQuery={query}
      >
        {(deals) => {
          return deals?.map((deal: Deal) => (
            <DealCard
              key={deal.id}
              deal={deal}
              salesStages={salesStages}
              users={users}
              onEdit={handleEditDeal}
            />
          ))
        }}
      </MobileInfiniteScrollView>

      {!!dealToEdit && (
        <MobileEditDealModal
          onClose={() => setDealToEdit(undefined)}
          onDealEdited={handleDealEdited}
          show={!!dealToEdit}
          deal={dealToEdit}
        />
      )}
    </>
  )
}
